/**
 * Thai Location API Client
 * Provides functions to fetch provinces, amphures, and districts
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface Province {
  id: number
  code: string
  name_th: string
  name_en: string
  geography_id: number
  created_at?: string
  updated_at?: string
}

export interface Amphure {
  id: number
  code: string
  name_th: string
  name_en: string
  province_id: number
  province_name_th?: string
  province_name_en?: string
  created_at?: string
  updated_at?: string
}

export interface District {
  id: number
  code: string
  name_th: string
  name_en: string
  amphure_id: number
  amphure_name_th?: string
  amphure_name_en?: string
  province_name_th?: string
  province_name_en?: string
  zipcode: string
  created_at?: string
  updated_at?: string
}

export interface LocationApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

/**
 * Get all provinces
 */
export async function getProvinces(): Promise<Province[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/locations/provinces`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`)
    }
    
    const data: LocationApiResponse<{ provinces: Province[]; total: number }> = await res.json()
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch provinces')
    }
    
    return data.data.provinces
  } catch (error) {
    console.error('Error fetching provinces:', error)
    throw error
  }
}

/**
 * Get amphures by province ID
 */
export async function getAmphuresByProvince(provinceId: number): Promise<Amphure[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/locations/provinces/${provinceId}/amphures`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`)
    }
    
    const data: LocationApiResponse<{ amphures: Amphure[]; total: number }> = await res.json()
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch amphures')
    }
    
    return data.data.amphures
  } catch (error) {
    console.error('Error fetching amphures:', error)
    throw error
  }
}

/**
 * Get districts by amphure ID
 */
export async function getDistrictsByAmphure(amphureId: number): Promise<District[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/locations/amphures/${amphureId}/districts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`)
    }
    
    const data: LocationApiResponse<{ districts: District[]; total: number }> = await res.json()
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch districts')
    }
    
    return data.data.districts
  } catch (error) {
    console.error('Error fetching districts:', error)
    throw error
  }
}

/**
 * Search locations by zipcode
 */
export async function getLocationsByZipcode(zipcode: string): Promise<District[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/locations/zipcode/${zipcode}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`)
    }
    
    const data: LocationApiResponse<{ districts: District[]; total: number; zipcode: string }> = await res.json()
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to search by zipcode')
    }
    
    return data.data.districts
  } catch (error) {
    console.error('Error searching by zipcode:', error)
    throw error
  }
}

/**
 * Validate address combination
 */
export async function validateAddress(address: {
  province_id: number
  amphure_id: number
  district_id: number
  zipcode: string
}): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/locations/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(address),
    })
    
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`)
    }
    
    const data: LocationApiResponse<{ valid: boolean }> = await res.json()
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to validate address')
    }
    
    return data.data.valid
  } catch (error) {
    console.error('Error validating address:', error)
    return false
  }
}
